import React, { useMemo } from 'react';
import { useForm } from '@inertiajs/react';
import { Button } from '../../components/ui/button';
import {
    Combobox,
    ComboboxContent,
    ComboboxEmpty,
    ComboboxInput,
    ComboboxItem,
    ComboboxList,
    ComboboxTrigger,
    ComboboxValue,
} from '../../components/ui/combobox';
import { Input } from '../../components/ui/input';
import ValidationError from '../../components/ValidationError';
import { useTrans } from '../../hooks/useTrans';

export function ContactsForm({ debtors, contact }) {
    const translate = useTrans();
    const isEditing = !!contact?.id;

    const debtorOptions = useMemo(() => debtors.map((debtor) => ({
        value: debtor.id,
        label: `${debtor.relation_number} - ${debtor.company_name}`,
    })), [debtors]);

    const { data, setData, post, put, processing, errors } = useForm({
        debtor: contact?.debtorId || '',
        name: contact?.name || '',
    });

    function handleSubmit(e) {
        e.preventDefault();
        if (isEditing) {
            put(`/contacts/${contact.id}`);
        } else {
            post('/contacts/toevoegen');
        }
    }

    return (
        <div className="max-w-[1200px] mx-auto px-4">
            <div className="bg-white rounded-[3px] border border-[#bbb]">
                <div className="p-[30px] pb-0">
                    <h3 className="text-2xl font-bold text-[#003545] mb-6">
                        {isEditing ? translate('Contactpersoon aanpassen') : translate('Contactpersoon aanmaken')}
                    </h3>
                </div>

                <div className="px-[30px] pb-[30px]">
                    <form onSubmit={handleSubmit} className="space-y-6">
                        <div className="flex items-center gap-4">
                            <label className="w-48 text-sm font-bold text-[#003545] shrink-0">
                                {translate('Debiteur')} *
                            </label>
                            <div className="w-full max-w-md">
                                <Combobox
                                    items={debtorOptions}
                                    value={debtorOptions.find((o) => String(o.value) === String(data.debtor)) ?? null}
                                    onValueChange={(item) => setData('debtor', item?.value ?? '')}
                                    itemToStringValue={(item) => item.label}
                                    disabled={isEditing}
                                >
                                    <ComboboxTrigger render={
                                        <Button variant="outline" className="w-full justify-between font-normal rounded-sm border-[#bbb] shadow-none" disabled={isEditing}>
                                            <ComboboxValue placeholder={translate('Selecteer een optie')} />
                                        </Button>
                                    } />
                                    <ComboboxContent>
                                        <ComboboxInput showTrigger={false} placeholder={translate('Zoeken...')} />
                                        <ComboboxEmpty>{translate('Geen resultaten.')}</ComboboxEmpty>
                                        <ComboboxList>
                                            {(item) => (
                                                <ComboboxItem key={item.value} value={item}>
                                                    {item.label}
                                                </ComboboxItem>
                                            )}
                                        </ComboboxList>
                                    </ComboboxContent>
                                </Combobox>
                                <ValidationError message={errors.debtor} />
                            </div>
                        </div>

                        <div className="flex items-center gap-4">
                            <label className="w-48 text-sm font-bold text-[#003545] shrink-0">
                                {translate('Contactpersoon')} *
                            </label>
                            <div className="w-full max-w-md">
                                <Input
                                    value={data.name}
                                    onChange={(e) => setData('name', e.target.value)}
                                    required
                                    className="font-normal"
                                />
                                <ValidationError message={errors.name} />
                            </div>
                        </div>

                        <div>
                            <Button variant="primary" type="submit" disabled={processing}>
                                {translate('Opslaan')}
                            </Button>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
}
